import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Calendar, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

interface Project {
  id: string;
  name: string;
  status: string;
  start_date?: string | null;
  target_end_date?: string | null;
  actual_end_date?: string | null;
  progress_percentage?: number | null;
  organizations?: { name: string } | null;
  organizationName?: string | null;
  org_id?: string | null;
}

interface RealisatieTimelineProps {
  projects: Project[];
}

const STATUS_COLORS: Record<string, string> = {
  definitief_ontwerp: 'bg-blue-500',
  bouwvoorbereiding: 'bg-yellow-500',
  bouwuitvoering: 'bg-orange-500',
};

const STATUS_LABELS: Record<string, string> = {
  definitief_ontwerp: 'Definitief ontwerp',
  bouwvoorbereiding: 'Bouwvoorbereiding',
  bouwuitvoering: 'Bouwuitvoering',
};

export function RealisatieTimeline({ projects }: RealisatieTimelineProps) {
  // Filter projects with dates and sort by start date
  const timelineProjects = projects
    .filter(p => p.start_date || p.target_end_date)
    .sort((a, b) => {
      const dateA = new Date(a.start_date || a.target_end_date || 0);
      const dateB = new Date(b.start_date || b.target_end_date || 0);
      return dateA.getTime() - dateB.getTime();
    });

  if (timelineProjects.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Geen tijdlijn data beschikbaar</p>
          <p className="text-sm">Voeg start- en einddatums toe aan projecten om de tijdlijn te zien</p>
        </div>
      </div>
    );
  }

  const today = new Date();

  return (
    <div className="space-y-4">
      {/* Today Indicator */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-primary">
            <Calendar className="h-4 w-4" />
            <span className="font-medium">Vandaag: {format(today, 'dd MMMM yyyy', { locale: nl })}</span>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <div className="space-y-4">
        {timelineProjects.map((project) => {
          const startDate = project.start_date ? new Date(project.start_date) : null;
          const targetDate = project.target_end_date ? new Date(project.target_end_date) : null;
          const actualDate = project.actual_end_date ? new Date(project.actual_end_date) : null;
          const progress = project.progress_percentage || 0;

          const isOverdue = targetDate && !actualDate && today > targetDate;
          const isCompleted = !!actualDate;
          
          const statusColor = STATUS_COLORS[project.status] ?? 'bg-gray-500';
          const organizationName = project.organizationName || project.organizations?.name;

          return (
            <Card key={project.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{project.name}</CardTitle>
                    {organizationName && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="h-3 w-3" />
                        {organizationName}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusColor}>
                      {STATUS_LABELS[project.status] || project.status}
                    </Badge>
                    {isOverdue && (
                      <Badge variant="destructive">Vertraagd</Badge>
                    )}
                    {isCompleted && (
                      <Badge variant="default" className="bg-green-600">
                        Afgerond
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Dates */}
                <div className="flex items-center gap-6 text-sm">
                  {startDate && (
                    <div>
                      <span className="text-muted-foreground">Start: </span>
                      <span className="font-medium">
                        {format(startDate, 'dd MMM yyyy', { locale: nl })}
                      </span>
                    </div>
                  )}
                  {targetDate && (
                    <div>
                      <span className="text-muted-foreground">Doel: </span>
                      <span className="font-medium">
                        {format(targetDate, 'dd MMM yyyy', { locale: nl })}
                      </span>
                    </div>
                  )}
                  {actualDate && (
                    <div>
                      <span className="text-muted-foreground">Afgerond: </span>
                      <span className="font-medium">
                        {format(actualDate, 'dd MMM yyyy', { locale: nl })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Progress Bar */}
                {!isCompleted && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">Voortgang</span>
                      </div>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${statusColor} transition-all duration-500`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Visual Timeline */}
                {startDate && targetDate && (
                  <div className="relative pt-4">
                    <div className="h-1 bg-muted rounded-full">
                      {/* Progress line */}
                      <div
                        className={`h-full ${statusColor} rounded-full transition-all`}
                        style={{ width: `${progress}%` }}
                      />
                      
                      {/* Today marker */}
                      {!isCompleted && today >= startDate && today <= targetDate && (
                        <div
                          className="absolute top-0 -translate-y-1/2"
                          style={{
                            left: `${((today.getTime() - startDate.getTime()) / (targetDate.getTime() - startDate.getTime())) * 100}%`,
                          }}
                        >
                          <div className="w-px h-8 bg-primary" />
                          <div className="w-2 h-2 bg-primary rounded-full absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
